import React, { useState } from "react";
import CropDrawer from "./CropDrawer";

// Same mapping as backend — kept in sync
const FIXTURE_MAP = {
    gondola:               { checkTypes: 'GONDOLA_AUDIT,GONDOLA_PLANOGRAM,SHELF_PRESENTATION', promptFile: 'GONDOLA.md' },
    gondola_shelving:      { checkTypes: 'GONDOLA_AUDIT,GONDOLA_PLANOGRAM,SHELF_PRESENTATION', promptFile: 'GONDOLA.md' },
    fridge_door:           { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    fridge_wall:           { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    freezer_door:          { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    freezer:               { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    ice_cream_freezer:     { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    pie_cabinet:           { checkTypes: 'PIE_CABINET', promptFile: 'PF_CABINET.md' },
    pie_face_cabinet:      { checkTypes: 'PIE_CABINET', promptFile: 'PF_CABINET.md' },
    coffee_station:        { checkTypes: 'COFFEE_STATION', promptFile: 'COFFEE.md' },
    pos_counter:           { checkTypes: 'POS_COUNTER,HEADCOUNT,UNIFORM', promptFile: 'POS_COUNTER.md' },
    counter_register:      { checkTypes: 'POS_COUNTER,HEADCOUNT,UNIFORM', promptFile: 'POS_COUNTER.md' },
    auto_wall:             { checkTypes: 'AUTO_WALL', promptFile: 'AUTO_WALL.md' },
    impulse_fridge:        { checkTypes: 'IMPULSE_FRIDGE', promptFile: 'IMPULSE_FRIDGE.md' },
    standalone_fridge:     { checkTypes: 'IMPULSE_FRIDGE', promptFile: 'IMPULSE_FRIDGE.md' },
    endcap:                { checkTypes: 'ENDCAPS,GONDOLA_PLANOGRAM', promptFile: 'ENDCAPS.md' },
    standalone_display:    { checkTypes: 'STANDALONE_DISPLAY', promptFile: 'STANDALONE_DISPLAY.md' },
    entry_exit:            { checkTypes: 'CUSTOMER_FLOW,DOOR_CLEANLINESS,SIGNAGE', promptFile: 'ENTRY.md' },
    forecourt:             { checkTypes: 'OUTDOOR_CLEANLINESS,SIGNAGE,PUMP_LOCKOUT,GROUNDS', promptFile: 'FORECOURT.md' },
    store_overview:        { checkTypes: 'STORE_OVERVIEW,CLEANLINESS,STOCK_ON_FLOOR', promptFile: 'OVERVIEW.md' },
    confectionery_wall:    { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    confectionery_display: { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    counter_display:       { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    refrigerated_drinks:   { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    magazine_rack:         { checkTypes: 'STANDALONE_DISPLAY', promptFile: 'STANDALONE_DISPLAY.md' },
};

const FIXTURE_TYPES = Object.keys(FIXTURE_MAP).sort();

function PresetEditModal({ preset, siteId, onSave, onClose }) {
    const [fixtureType, setFixtureType] = useState(preset.fixtureType || "");
    const [fixtureId, setFixtureId] = useState(preset.fixtureId || "");
    const [cropRegion, setCropRegion] = useState(preset.cropRegion || null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const mapping = FIXTURE_MAP[fixtureType];
    const hasChanges =
        fixtureType !== (preset.fixtureType || "") ||
        fixtureId !== (preset.fixtureId || "") ||
        JSON.stringify(cropRegion) !== JSON.stringify(preset.cropRegion || null);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const data = {};
            if (fixtureType !== (preset.fixtureType || "")) data.fixtureType = fixtureType;
            if (fixtureId !== (preset.fixtureId || "")) data.fixtureId = fixtureId;
            if (JSON.stringify(cropRegion) !== JSON.stringify(preset.cropRegion || null)) {
                data.cropRegion = cropRegion;
            }
            await onSave(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="preset-edit-modal" onClick={(e) => e.stopPropagation()}>
                <div className="preset-edit-modal-header">
                    <h3>Edit Preset: {preset.presetName}</h3>
                    <button className="modal-close-btn" onClick={onClose}>ESC</button>
                </div>

                <div className="preset-edit-modal-body">
                    <div className="preset-edit-form">
                        <div className="preset-edit-field">
                            <label>Fixture Type</label>
                            <select
                                className="filter-select"
                                value={fixtureType}
                                onChange={(e) => setFixtureType(e.target.value)}
                            >
                                <option value="">-- Select --</option>
                                {FIXTURE_TYPES.map((ft) => (
                                    <option key={ft} value={ft}>
                                        {ft.replace(/_/g, " ")}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {mapping && (
                            <div className="preset-edit-derived">
                                <div>
                                    <span className="preset-edit-derived-label">Check Types:</span>
                                    <span>{mapping.checkTypes}</span>
                                </div>
                                <div>
                                    <span className="preset-edit-derived-label">Prompt File:</span>
                                    <span>{mapping.promptFile}</span>
                                </div>
                            </div>
                        )}

                        <div className="preset-edit-field">
                            <label>Fixture Description</label>
                            <input
                                type="text"
                                className="filter-select"
                                value={fixtureId}
                                onChange={(e) => setFixtureId(e.target.value)}
                                placeholder="e.g. Snack Gondola - Bay 1"
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div className="preset-edit-field">
                            <label>Crop Region</label>
                            <CropDrawer
                                imageUrl={preset.referenceImageUrl}
                                initialCrop={cropRegion}
                                onChange={setCropRegion}
                            />
                        </div>
                    </div>
                </div>

                {error && <div className="wizard-error">{error}</div>}

                <div className="preset-edit-modal-footer">
                    <button className="action-cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="action-submit"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                    >
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PresetEditModal;
